import React, {useEffect, useMemo} from "react";
import {useDataSource} from "src/core/data-sources/decorators/withData";
import styled from "styled-components";
import PaymentSource from "src/core/payments/PaymentSource";
import {PrimaryLoadingButton} from "src/core/common/components/elements/button/PrimaryButton";
import Loader from "src/core/common/components/elements/Loader";
import PaymentIcon from "react-payment-icons-inline";
import {cardTypes} from "src/core/payments/CardComponents/cardTypes";
import {Trash2} from "styled-icons/feather/Trash2";
import useAuthentication from "src/core/authentication/hooks/useAuthentication";
import FallbackCardIcon from "src/core/payments/CardComponents/card-icons/FallbackCardIcon";
import {NullPaymentSource} from "src/core/payments/models/paymentSource";

function CardListField({
  order,
  showForm,
  input,
  loading,
  disabled,
  readOnly,
  paymentOption,
  form,
  dataSource,
  hideAddButton = false,
  profile = false,
}) {
  const [, authApi] = useAuthentication();
  const {
    data: paymentSources,
    meta,
    refresh,
  } = useDataSource(
    {},
    {
      dataSource: dataSource,
      requestCondition: authApi.isLoggedIn(),
    }
  );

  const pSources = useMemo(() => {
    if (order && order.isPaid()) {
      return [
        order.getPaymentSpecification()?.getPaymentInfo() || new NullPaymentSource(),
      ];
    }
    return paymentSources;
  }, [order, paymentSources]);

  useEffect(() => {
    return paymentOption?.subscribe(({event}) => {
      const refreshEvents = [
        "removed-account-success",
        "changed-default-account-success",
      ];

      if (refreshEvents.indexOf(event) > -1) refresh();
    });
  }, [paymentOption, refresh]);

  useEffect(() => {
    if (pSources) {
      const source = pSources.find(source => source.isDefault) || pSources[0];
      // eslint-disable-next-line
      form?.mutators?.setValue("payment.source", source);
    }
    // eslint-disable-next-line
  }, [pSources]);

  if (meta.loading && (!pSources || pSources.length === 0)) {
    return (
      <Container>
        <Loader />
      </Container>
    );
  }

  async function removeAccount(paymentSource) {
    if (loading || disabled) return;
    const currentPaymentSource = form.getState().values.payment?.source;
    if (currentPaymentSource && currentPaymentSource.id === paymentSource.id) {
      form.mutators.setValue("payment.source", undefined);
    }

    await paymentOption.removeAccount(paymentSource);
  }

  async function changeDefaultAccount(paymentSource) {
    if (loading || disabled) return;
    await paymentOption.setDefaultAccount(paymentSource);
  }

  if (!pSources || pSources.length === 0) {
    return (
      <Container>
        <span>No cards available</span>
        {!hideAddButton && (
          <NewCardButton
            loading={loading}
            disabled={disabled}
            onClick={showForm}
            type={"button"}
            data-cy={"newSourcePay"}
            label="Add new card"
          />
        )}
      </Container>
    );
  }

  function getPaymentIcon(iconName) {
    if (!cardTypes[iconName]) return <FallbackCardIcon />;

    return <PaymentIcon icon={cardTypes[iconName]} style={{width: 40, height: 25}} />;
  }

  const paymentDate =
    order && order.isPaid() ? order.getPaymentSpecification().getPaymentDate() : null;

  return (
    <Container profile={profile}>
      <ListContainer>
        {pSources.map((source, index) => (
          <ListItem key={index}>
            <PaymentSource
              readOnly={order && order.isPaid()}
              isDefault={profile && source.isDefault}
              isExpired={source.isExpired}
              institution={null}
              institutionName={source.getMaskLabel()}
              label={source.getExpiryLabel()}
              paymentDate={paymentDate}
              icon={getPaymentIcon(source.institution)}
              onClick={() => {
                !disabled && !source.isExpired && input.onChange(source);
                profile && changeDefaultAccount(source);
              }}
              checked={source.isSelected(input.value)}
            />
            {!readOnly && (
              <CloseIcon
                loading={loading || disabled}
                onClick={() => removeAccount(source)}
              />
            )}
          </ListItem>
        ))}
        {!readOnly && !hideAddButton && (
          <PrimaryLoadingButton label="Add new card" onClick={showForm} />
        )}
      </ListContainer>
    </Container>
  );
}

const Container = styled.div`
  padding: ${({theme}) => theme.v2.spacing(4)} 0 0 0;

  width: auto !important;
  border: ${({profile}) => (profile ? "none" : "1px solid #ebebeb")};
  border-radius: 10px;
  padding: ${({theme}) => theme.v2.spacing(8)};
  display: flex;
  row-gap: ${({theme}) => theme.v2.spacing(2)};
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  > button {
    max-width: 200px;
  }
`;

const ListContainer = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 16px;

  .payment-source__label {
    margin-top: -5px;
  }
`;
const ListItem = styled.li`
  display: flex;
  width: 100%;
  align-items: center;
  font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
  justify-content: space-between;
`;

const NewCardButton = styled(PrimaryLoadingButton)`
  margin-top: ${({theme}) => theme.v2.spacing(2)};
  background-color: #ffffff;
  color: ${({theme}) => theme.v2.color.primary};
  border: 1px solid ${({theme}) => theme.v2.color.primary};
  > *:first-child {
    margin-right: ${({theme}) => theme.v2.spacing(2)};
  }
  &:hover {
    opacity: 0.6;
    color: ${({theme}) => theme.v2.color.primary};
  }
`;

const CloseIcon = styled(Trash2)`
  cursor: ${({loading}) => (loading ? "not-allowed" : "pointer")};
  height: 22px;
  width: 22px;
  margin-left: ${({theme}) => theme.v2.spacing(2)};
`;

export default CardListField;
